import djuboIcon from "../images/djubo.ico"
import axisRoomsIcon from "../images/axisrooms.png"
import useSWR from "swr"
import { useXHR, XHRInstance } from "@sembark-travel/xhr"
import { XHR as IntegrationsXHR, TIntegration } from "../Integrations/store"
export const DJUBO = "cm_djubo" as const
export const AXISROOMS = "cm_axisrooms" as const

// Channel manager a hotel's inventory can be managed by
export type TChannelManager = typeof DJUBO | typeof AXISROOMS

export type TChannelManagerItem = {
	channel_manager: TChannelManager
	name: string
}

// Option of the channel manager select. `id` is null when not connected to any.
export type TChannelManagerOption = {
	id: TChannelManager
	name: string
}

export function channelManagersXHR(xhr: XHRInstance) {
	return {
		async list(): Promise<Array<TChannelManagerItem>> {
			return xhr
				.get<{ data: Array<TChannelManagerItem> }>("/channel-managers")
				.then((resp) => resp.data.data)
		},
	}
}

/**
 * Channel managers we can talk to. Add an entry here, and a branch in
 * `ChannelManagerSimulator`, to support another one.
 */
export const CHANNEL_MANAGERS: Record<
	string,
	{ id: string; name: string; iconUrl?: string }
> = {
	[DJUBO]: {
		id: DJUBO,
		name: "Djubo",
		iconUrl: djuboIcon,
	},
	[AXISROOMS]: {
		id: AXISROOMS,
		name: "AxisRooms",
		iconUrl: axisRoomsIcon,
	},
}

/**
 * Display name of a hotel's channel manager, or `null` when the hotel is not
 * connected to one.
 */
export function getChannelManagerName(
	channelManager: string | null | undefined
): string | null {
	if (!channelManager) {
		return null
	}
	return CHANNEL_MANAGERS[channelManager]?.name || channelManager
}

/**
 * Actions the Djubo channel manager can push to us. These match the `actionId`
 * accepted by the Djubo webhook api.
 */
export const DJUBO_ACTIONS = {
	// pushes: queued and applied by a worker, so they only answer "ok"
	UPDATE_ROOM_RATES: "cm_updateroomrates",
	UPDATE_ROOM_INVENTORY: "cm_updateroominventory",
	STOP_SELL_BY_RATE_PLAN: "cm_stopsellbyrateplan",
	// reads: answered straight away with the current data
	GET_ROOM_AND_RATE_PLAN_LIST: "cm_hotelallroomcode",
	GET_ROOM_RATES: "cm_roomrates",
	GET_ROOM_INVENTORY: "cm_roominventory",
} as const

export type TDjuboAction = (typeof DJUBO_ACTIONS)[keyof typeof DJUBO_ACTIONS]

export type TDjuboWebhookConfig = {
	url: string
	source: string
	auth: string
}

/**
 * Weekdays in the order Djubo expect them inside the `days` bitmask, which
 * start at Monday instead of the usual Sunday.
 */
export const DJUBO_WEEKDAYS = [
	{ id: "mon", name: "Mon" },
	{ id: "tue", name: "Tue" },
	{ id: "wed", name: "Wed" },
	{ id: "thu", name: "Thu" },
	{ id: "fri", name: "Fri" },
	{ id: "sat", name: "Sat" },
	{ id: "sun", name: "Sun" },
] as const

export type TDjuboWeekday = (typeof DJUBO_WEEKDAYS)[number]["id"]

/**
 * Convert the selected weekdays into the Monday-first bitmask string that Djubo
 * send, e.g. `"1111100"` for Mon-Fri.
 */
export function weekdaysToDjuboBitmask(
	weekdays: Array<TDjuboWeekday> | undefined
): string {
	// an empty selection mean no day, not every day: silently widening it to
	// the whole week would push rates the caller never asked for
	const selected = weekdays || []
	return DJUBO_WEEKDAYS.map((d) => (selected.includes(d.id) ? "1" : "0")).join(
		""
	)
}

/**
 * Fetch the Djubo integration of the tenant along with the webhook credentials
 * needed to imitate the channel manager.
 */
export function useDjuboIntegration() {
	const xhr = useXHR()
	const { data, error, isValidating } = useSWR(
		`/api/integrations?platform=${DJUBO}`,
		() => IntegrationsXHR(xhr).get({ platform: DJUBO })
	)
	// a tenant can have more than one active Djubo integration, and the newest
	// one is the one being set up, so simulate with that
	const integration: TIntegration | undefined = (data?.data || [])
		.filter(
			(i) => i.platform === DJUBO && !i.paused_at && i.cm_djubo_webhook_config
		)
		.sort((a, b) => b.id - a.id)[0]
	return {
		integration,
		webhookConfig: integration?.cm_djubo_webhook_config,
		isFetching: !data && !error,
		isRefreshing: isValidating,
		error,
	}
}

export type TDjuboWebhookResponse = {
	status: number
	ok: boolean
	body: string
}

/**
 * Send a webhook call exactly the way the Djubo channel manager would.
 *
 * The webhook url is absolute and sit outside our api, and it authenticate with
 * the integration's own credentials instead of the user's session. Booleans
 * must be sent as 1/0 because our xhr client form-encode the body, where a
 * `false` would reach the server as the truthy string "false".
 */
export async function sendDjuboWebhook({
	xhr,
	config,
	action,
	requestData,
}: {
	xhr: XHRInstance
	config: TDjuboWebhookConfig
	action: TDjuboAction
	requestData: Record<string, unknown>
}): Promise<TDjuboWebhookResponse> {
	const response = await xhr.post(
		config.url,
		{
			actionId: action,
			typeId: "hotel",
			source: config.source,
			auth: config.auth,
			requestData,
		},
		{
			// the webhook's own error responses are the point of the simulator,
			// so keep them instead of throwing
			validateStatus: () => true,
		}
	)
	const { status, data } = response
	return {
		status,
		ok: status >= 200 && status < 300,
		body: typeof data === "string" ? data : JSON.stringify(data, null, 2),
	}
}

/**
 * The apis AxisRooms calls on us. These are the paths it addresses, and the
 * action the simulator stand in for.
 */
export const AXISROOMS_ACTIONS = {
	// reads: answered straight away with what we currently hold
	PRODUCT_INFO: "productInfo",
	RATE_PLAN_INFO: "ratePlanInfo",
	// pushes: taken, then applied by a worker
	INVENTORY_UPDATE: "inventoryUpdate",
	RATE_UPDATE: "rateUpdate",
	RESTRICTION_UPDATE: "restrictionUpdate",
} as const

export type TAxisRoomsAction =
	(typeof AXISROOMS_ACTIONS)[keyof typeof AXISROOMS_ACTIONS]

/**
 * The occupancies AxisRooms price a room at. The first lot are named after how
 * many guests sleep in the room, the rest are everyone beyond that.
 *
 * All of them are offered, including ones a given room is not sold at, so that
 * the api's own refusal can be seen.
 */
export const AXISROOMS_OCCUPANCIES = [
	"SINGLE",
	"DOUBLE",
	"TRIPLE",
	"QUAD",
	"PENTA",
	"HEXA",
	"HEPTA",
	"OCTA",
	"EXTRAADULT",
	"EXTRAADULT2",
	"EXTRAADULT3",
	"EXTRACHILD",
	"EXTRACHILD2",
	"EXTRACHILD3",
	"EXTRAINFANT",
	"EXTRABED",
] as const

/**
 * The selling controls AxisRooms apply. A Status, arrival or departure block
 * is opened or closed; a minimum length of stay is a number of nights.
 */
export const AXISROOMS_RESTRICTION_TYPES = [
	"Status",
	"CTA",
	"CTD",
	"MLOS",
] as const

export type TAxisRoomsResponse = {
	status: number
	ok: boolean
	body: string
}

/**
 * Send a call the way AxisRooms would make it.
 *
 * Unlike Djubo, the browser hold no credentials: AxisRooms authenticate with
 * one key per environment, so the request goes to our own authenticated
 * simulator endpoint, which put the key on it and hand it to the api that
 * would have taken it.
 */
export async function sendAxisRoomsRequest({
	xhr,
	action,
	payload,
}: {
	xhr: XHRInstance
	action: TAxisRoomsAction
	payload: Record<string, unknown>
}): Promise<TAxisRoomsResponse> {
	const response = await xhr.post(`/axisrooms-simulator/${action}`, payload, {
		// the api's own refusals are the point of the simulator, so keep them
		// instead of throwing
		validateStatus: () => true,
	})
	const { status, data } = response
	return {
		status,
		ok: status >= 200 && status < 300,
		body: typeof data === "string" ? data : JSON.stringify(data, null, 2),
	}
}
